/**
 * Windermere Veneer Configuration
 *
 * Extended configuration for the Windermere theme veneer.
 * This includes feature flags, layout options, assets, and
 * content that is specific to Windermere Preparatory School.
 */

export const windermereVeneerConfig = {
  // Theme identification
  name: 'windermere',
  displayName: 'Windermere Preparatory School',
  parentBrand: 'Nord Anglia Education',

  // Feature flags - enable/disable Windermere-specific features
  features: {
    megaMenu: true,              // Full-width dropdown mega menu
    searchBar: true,             // Search bar in header
    scrollEffects: true,         // Header changes on scroll
    heroSection: true,           // Large hero section on landing
    quickActions: true,          // Quick action buttons on landing
    shopByGrade: true,           // Grade-level shopping dropdown
    shopByCollection: true,      // Collection-based shopping section
    contactInMenu: true,         // Contact info in navigation menu
    instagramLink: true,         // Link to Instagram
    promoBanner: true,           // Promotional banner at top
  },

  // Color palette - Windermere navy scheme
  colors: {
    primary: '#02233c',           // Windermere navy
    primaryLight: '#1a3a5c',      // Lighter navy
    primaryDark: '#011829',       // Darker navy
    accent: '#9bd3dd',            // Teal accent
    accentHover: '#7bc4d0',       // Darker teal
    // Primary action buttons - dark navy for white text contrast
    primaryActionBg: '#02233c',   // Dark navy - ensures readable white text
    primaryActionBgHover: '#011a2c', // Darker navy for hover
    headerBg: '#02233c',          // Header background
    menuBg: '#02233c',            // Dropdown menu background
    menuBorder: '#0b3b57',        // Menu border color
    menuText: '#ffffff',          // Menu text color
    menuTextHover: '#9bd3dd',     // Menu text hover
    ctaButton: '#9bd3dd',         // CTA button background
    ctaButtonText: '#02233c',     // CTA button text
    ctaButtonHover: '#ffffff',    // CTA button hover bg
  },

  // Asset paths - Windermere-specific images and icons
  assets: {
    logo: '/nord-anglia-school_windermere_logo_horizontal_white.png',
    heroBackground: '/images/campus-store.png',
    heroBackgroundAlt: '/images/campus-store-interior.jpg',
    favicon: '/images/windermere-favicon.ico',

    // Category/collection icons (SVG)
    icons: {
      shop: '/images/spirit-items.svg',
      peUniforms: '/images/pe-uniforms.svg',
      coldDayGear: '/images/cold-day-gear.svg',
      spiritItems: '/images/spirit-items.svg',
      newToSchool: '/images/new-to-school.svg',
      seasonal: '/images/seasonal-collection.svg',
    },

    // Student/lifestyle images
    lifestyle: {
      studentsEntering: '/images/group-of-students-entering-building.jpg',
      studentsEmbracing: '/images/group-of-happy-elementary-students-standing-embrac-2024-12-13-21-50-43-utc.jpg',
      studentSupplies: '/images/handsome-boy-child-putting-new-drawing-supplies-in-2025-05-15-12-29-02-utc.jpg',
    },
  },

  // Contact information
  contact: {
    email: 'campus.store@windermereprep.com',
    phone: '(407) 905-7737',
    phoneExtension: null,
    address: '6189 Winter Garden-Vineland Road, Windermere, FL 34786',
    instagram: 'https://instagram.com/windermereprep',
    website: 'https://www.windermereprep.com',
  },

  // Content/copy for various UI elements
  content: {
    heroTitle: 'WINDERMERE E-COMMERCE STORE',
    heroSubtitle: 'WELCOME TO',
    heroDescription: 'Windermere Preparatory School is the leading international prep school in Orlando and part of the worldwide Nord Anglia family of premium international schools. We are dedicated to supporting and inspiring your child to reach their greatest potential.',
    ctaPrimary: 'SHOP UNIFORMS',
    ctaSecondary: 'SIGN IN FOR FASTER CHECKOUT',
    trustBand: {
      secure: 'Secure Payments',
      fast: 'Fast Checkout',
      guest: 'Guest Checkout',
    },
    quickActionsTitle: 'Quick Actions',
    quickActionsSubtitle: 'Find what you need quickly with our convenient category shortcuts',
    collectionsTitle: 'Shop by Collection',
    collectionsSubtitle: 'Explore our curated collections and find exactly what you need',
    brandStrip: 'Part of the Nord Anglia Education family',
  },

  // Navigation structure for mega menu
  navigation: {
    shop: {
      title: 'Shop',
      items: [
        { label: 'All Products', path: '/shop' },
        { label: 'Uniforms', path: '/shop?category=uniforms' },
        { label: 'PE Gear', path: '/shop?category=pe' },
        { label: 'Cold Day Gear', path: '/shop?category=cold-day' },
        { label: 'Spirit Items', path: '/shop?category=spirit', comingSoon: true },
      ],
    },
    browse: {
      title: 'Browse by',
      items: [
        { label: 'Gender (Boys, Girls, Unisex)', action: 'filterGender' },
        { label: 'Grade (Elementary, Middle, High)', action: 'filterGrade' },
      ],
    },
    discover: {
      title: 'Discover more',
      items: [
        { label: 'Pick up Procedure for online orders', path: '/info/pickup' },
        { label: 'Store Location', path: '/info/location' },
        { label: 'Return Policy', path: '/info/returns' },
        { label: 'Uniform Policy', path: '/info/uniform-policy' },
      ],
    },
  },

  // Quick action buttons for landing page
  quickActions: [
    { id: 'shop', label: 'Shop', description: 'Browse all products', icon: 'shopping-bag', path: '/shop' },
    { id: 'pe', label: 'PE Uniforms', description: 'Athletic wear & gear', icon: 'user', path: '/shop?category=pe' },
    { id: 'cold', label: 'Cold Day Gear', description: 'Jackets & warm clothing', icon: 'moon', path: '/shop?category=cold-day' },
    { id: 'spirit', label: 'Spirit Items', description: 'Coming soon', icon: 'heart', disabled: true },
  ],

  // Grade levels for dropdown
  grades: [
    { value: 'pre-k', label: 'Pre-K' },
    { value: 'kindergarten', label: 'Kindergarten' },
    { value: '1st', label: '1st Grade' },
    { value: '2nd', label: '2nd Grade' },
    { value: '3rd', label: '3rd Grade' },
    { value: '4th', label: '4th Grade' },
    { value: '5th', label: '5th Grade' },
    { value: '6th', label: '6th Grade' },
    { value: '7th', label: '7th Grade' },
    { value: '8th', label: '8th Grade' },
    { value: '9th', label: '9th Grade' },
    { value: '10th', label: '10th Grade' },
    { value: '11th', label: '11th Grade' },
    { value: '12th', label: '12th Grade' },
  ],

  // Collections for "Shop by Collection" section
  collections: [
    { id: 'grade', label: 'Shop by Grade', description: null, type: 'dropdown', icon: 'book' },
    { id: 'seasonal', label: 'Seasonal Collection', description: 'Current season essentials', icon: 'sun' },
    { id: 'new-to-school', label: 'New to School', description: 'Everything you need to start', icon: 'book-open' },
    { id: 'pe', label: 'PE', description: 'Physical education gear', icon: 'bolt' },
    { id: 'cold-day', label: 'Cold Day Gear', description: 'Warm clothing & accessories', icon: 'moon' },
    { id: 'website', label: 'School Website', description: 'Visit our main site', type: 'external', icon: 'external-link' },
  ],
}

export default windermereVeneerConfig
